<script setup lang="ts">
import { onMounted, computed, watch, nextTick, onErrorCaptured, onUnmounted } from 'vue';
import { useRoute, RouterView, RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import * as Sentry from "@sentry/vue";

import Header from './layouts/Header.vue';
import Footer from './layouts/Footer.vue';
import useUserStore from "./stores/useUserStore";
import useAnalysisStore from "./stores/useAnalysis";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";
import LoadingOverlay from "./components/loading/LoadingOverlay.vue";
import AnalysisRunningWall from "./components/wall/AnalysisRunningWall.vue";
import { getUpgradeTransitionStatus } from './services/welcomeAnalysis';
import { removeLocaleFromPath } from './i18n/index.js';
import { APP_TOAST_GROUP } from './composables/useBrandLimitToast';

const { t } = useI18n();
const route = useRoute();
const userStore = useUserStore();
const analysisStore = useAnalysisStore();

const isAuthRoute = computed(() => {
  const basePath = removeLocaleFromPath(route.path);
  return basePath === '/login' || basePath === '/register' || basePath === '/reset-password';
});

// Combined loading state for the global overlay
const isLoadingGlobally = computed(() => userStore.isLoggingIn || userStore.isRegistering);
const loadingMessage = computed(() => {
  if (userStore.isLoggingIn) return t('logging_you_in');
  if (userStore.isRegistering) return t('creating_your_account');
  return t('loading'); // Fallback, though should not be hit if isLoadingGlobally is false
});

// Global welcome analysis wall state
const showGlobalAnalysisWall = computed(() => analysisStore.isWelcomeAnalysisRunning || analysisStore.welcomeAnalysisCompleted);
const isAnalysisComplete = computed(() => analysisStore.welcomeAnalysisCompleted);

// Start/stop polling based on wall visibility
watch(showGlobalAnalysisWall, async (show) => {
  if (show && !analysisStore.welcomeAnalysisCompleted) {
    await analysisStore.startWelcomeAnalysisPolling()
  } else {
    analysisStore.stopWelcomeAnalysisPolling()
  }
}, { immediate: true })

onUnmounted(() => {
  analysisStore.stopWelcomeAnalysisPolling()
})

onMounted(async () => {
  if (!userStore.isInitialized) {
    try {
      await userStore.initializeAuth();
    } catch (error) {
      console.error('App.vue: Error initializing user store:', error);
    }
  }
  
  // Check if there's an ongoing or completed welcome analysis
  if (userStore.isAuthenticated && (analysisStore.isWelcomeAnalysisRunning || analysisStore.welcomeAnalysisCompleted)) {
    try {
      const status = await getUpgradeTransitionStatus();
      // If analysis is no longer in progress, reset the store state
      // This handles page refresh after analysis completion
      if (status.status !== 'in_progress') {
        analysisStore.resetWelcomeAnalysis();
      }
    } catch (error) {
      console.error('App.vue: Error checking upgrade status:', error);
      // On error, reset to avoid stuck state
      analysisStore.resetWelcomeAnalysis();
    }
  }
});

const isStoreInitialized = computed(() => userStore.isInitialized);

// Error boundary to capture errors from child components
onErrorCaptured((err, instance, info) => {
  if (import.meta.env.PROD) {
    Sentry.captureException(err, {
      extra: {
        componentName: instance?.$options?.name || 'UnknownComponent',
        errorInfo: info,
        route: route?.path,
      },
    });
  }
  console.error('Captured error in App.vue:', err, 'Info:', info);
  // Return false to prevent error propagation
  return false;
});

// Seamless layout - header and footer always shown

// Removed redirect logic from App.vue watcher - router guard now handles all redirects
// This prevents conflicts and loops between App.vue and router guard

// Watch for route changes to clear loading states when navigation is complete
watch(
  () => route.path,
  async (newPath, oldPath) => {
    // Clear loading states when successfully navigating away from login/register pages
    // and when the loading states are active (indicating a successful auth flow)
    if (oldPath && (oldPath.includes('/login') || oldPath.includes('/register')) && 
        !newPath.includes('/login') && !newPath.includes('/register') &&
        (userStore.isLoggingIn || userStore.isRegistering)) {
      // Use nextTick to ensure the new route component is mounted before clearing loading
      await nextTick();
      // Add a small delay to ensure the component is fully rendered
      setTimeout(() => {
        userStore.clearAuthLoadingStates();
      }, 50);
    }
  }
);
</script>

<template>
  <div class="layout">
    <div v-if="!isStoreInitialized" class="loading-container">
      <div class="spinner">
        <div class="bounce1"></div>
        <div class="bounce2"></div>
        <div class="bounce3"></div>
      </div>
    </div>
    
    <template v-else>
      <!-- Header only shown when user is not authenticated and not on auth pages -->
      <!-- <Header v-if="!userStore.isAuthenticated" /> -->
      <Header v-if="!userStore.isAuthenticated && !isAuthRoute" />
      
      <main class="main-content" :class="{ 'main-content-authenticated': userStore.isAuthenticated }">
        <div v-if="!userStore.isAuthenticated">
          <router-view />
        </div>
        <div v-else class="authenticated-container">
          <router-view v-slot="{ Component }">
            <Suspense>
              <component :is="Component" />
              <template #fallback>
                <div class="loading-container">
                  <div class="spinner">
                    <div class="bounce1"></div>
                    <div class="bounce2"></div>
                    <div class="bounce3"></div>
                  </div>
                </div>
              </template>
            </Suspense>
          </router-view>
        </div>
      </main>
      
      <!-- Footer only shown when user is not authenticated and not on auth pages -->
      <!-- <Footer v-if="!userStore.isAuthenticated" class="footer" /> -->
      <Footer v-if="!userStore.isAuthenticated && !isAuthRoute" class="footer" />
    </template>

    <!-- Global Loading Overlay for login/registration -->
    <LoadingOverlay v-if="isLoadingGlobally" :message="loadingMessage" />

    <!-- Global Analysis Running Wall -->
    <AnalysisRunningWall 
      v-if="showGlobalAnalysisWall" 
      :brand-id="analysisStore.welcomeAnalysisBrandId || 0"
      :is-complete="isAnalysisComplete"
    />

  </div>
  <Toast :group="APP_TOAST_GROUP" position="bottom-right">
    <template #message="{ message }">
      <div class="app-toast-message">
        <p v-if="message.summary" class="app-toast-summary">{{ message.summary }}</p>
        <p v-if="message.detail" class="app-toast-detail">{{ message.detail }}</p>
        <RouterLink
          v-if="message.data?.showPricingLink && message.data?.pricingPath"
          :to="message.data.pricingPath"
          class="app-toast-pricing-link"
        >
          {{ t('add_brand_upgrade_link') }}
        </RouterLink>
      </div>
    </template>
  </Toast>
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="bg-white text-gray-800 p-6 rounded-lg shadow-lg max-w-md mx-auto">
        <div class="flex items-center mb-4">
          <i class="pi pi-exclamation-triangle text-yellow-500 text-2xl mr-3"></i>
          <h2 class="text-xl font-semibold">{{ message.header }}</h2>
        </div>
        <p class="mb-6">{{ message.message }}</p>
        <div class="flex justify-end space-x-3">
          <button
            @click="rejectCallback"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            {{ message.rejectLabel || t('cancel') }}
          </button>
          <button
            @click="acceptCallback"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {{ message.acceptLabel || t('proceed') }}
          </button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>

<style scoped>
.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex: 1;
}

/* Main content styles when authenticated (no header/footer) */
.main-content-authenticated {
  margin-top: 0;
  padding-top: 0;
}

/* .authenticated-container styles removed for seamless layout */

.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 9999;
}

.spinner {
  width: 70px;
  text-align: center;
}

.spinner > div {
  width: 18px;
  height: 18px;
  background-color: var(--primary-color);
  border-radius: 100%;
  display: inline-block;
  animation: sk-bouncedelay 1.4s infinite ease-in-out both;
  margin: 0 2px;
}

.spinner .bounce1 {
  animation-delay: -0.32s;
}

.spinner .bounce2 {
  animation-delay: -0.16s;
}

@keyframes sk-bouncedelay {
  0%, 80%, 100% { transform: scale(0); } 
  40% { transform: scale(1.0); }
}

/* Mobile responsive styles removed for seamless layout */
</style>

<style>
.app-toast-message {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.app-toast-summary,
.app-toast-detail {
  margin: 0;
  line-height: 1.45;
}

.app-toast-pricing-link {
  font-weight: 600;
  text-decoration: underline;
  color: var(--p-primary-color, #2563eb);
}

.app-toast-pricing-link:hover {
  text-decoration: none;
}
</style>
